<template>
    <div class="p-3">
        <h4 class="text-dark">Control Center: CPT/Funding</h4>

        <table-input-widget
            v-for="w in [widgetCptCodes]"
            :ref="w.ref"
            :key="w.key"
            :no-collapse="true"
            :loading="w.loading"
            :title="w.title"
            :fields="w.fields"
            :data-source="w.dataSource"
            :table-class="w.tableClass"
            :widget-errors="w.errors"
            @delete="deleteWidgetEntry(w.ref, $event)"
            @edit="editWidgetEntry(w.ref, $event)"
        >
            <template slot="bottom">
                <form class="row form-group">
                    <div v-for="f in w.fields" :class="[`col-${f.widthBase12}`, f.class]">
                        <template v-if="f.prop === 'code'">
                            <div class="d-flex flex-wrap w-100">
                                <input
                                    v-model="w.payload.code"
                                    :disabled="w.payload.id"
                                    type="text"
                                    class="form-control form-control-sm px-1"
                                    placeholder="Code"
                                    style="max-width: 4rem;"
                                />
                                <span class="">-</span>
                                <input
                                    v-model="w.payload.code_modifier"
                                    :disabled="w.payload.id"
                                    type="text"
                                    class="form-control form-control-sm px-1"
                                    style="max-width: 2.8rem;"
                                    v-tooltip="'Optional modifier. e.g. A1, A2'"
                                    v-mask="'XX, XX'"
                                    @input="
                                        helpers.forceUpper($event, w.payload, 'code_modifiers', vue)
                                    "
                                />
                            </div>
                        </template>
                        <template v-else="">
                            <multi-field
                                v-if="f.type !== 'select-multiple'"
                                :name="f.prop"
                                :disabled="f.prop === 'code' && w.payload.id"
                                v-model="w.payload[f.prop]"
                                :type="f.type"
                                :placeholder="f.placeholder"
                                :options="f.options"
                                @focus="setMainAction(w.ref, 'save')"
                            ></multi-field>
                            <select
                                v-else=""
                                multiple
                                v-model="w.payload[f.prop]"
                                class="form-control form-control-sm taller"
                                @focus="setMainAction(w.ref, 'save')"
                            >
                                <option
                                    v-for="o in f.options.filter(
                                        opt =>
                                            w.payload.id ||
                                            !unavailableTherapistTypes.includes(opt.value)
                                    )"
                                    :value="o.value"
                                    >{{ o.title }}</option
                                >
                            </select>
                        </template>
                    </div>
                    <div class="col-1 px-0">
                        <input-actions-widget
                            :widget="w"
                            :main="w.mainAction"
                            @cancel-widget-edit="cancelWidgetEdit(w.ref)"
                            @save-widget-new-entry="saveWidgetNewEntry(w.ref)"
                            @delete-widget-entry="deleteWidgetEntry(w.ref)"
                        ></input-actions-widget>
                    </div>
                </form>
            </template>
        </table-input-widget>

        <table-input-widget
            v-for="w in [widgetDiagnosticCodes, widgetServiceCodes]"
            :ref="w.ref"
            :key="w.key"
            :no-collapse="true"
            :loading="w.loading"
            :title="w.title"
            :fields="w.fields"
            :data-source="w.dataSource"
            :table-class="w.tableClass"
            :widget-errors="w.errors"
            @delete="deleteWidgetEntry(w.ref, $event)"
            @edit="editWidgetEntry(w.ref, $event)"
        >
            <template slot="bottom">
                <form class="row form-group">
                    <div v-for="f in w.fields" :class="[`col-${f.widthBase12}`, f.class]">
                        <multi-field
                            :name="f.prop"
                            :disabled="f.prop === 'code' && w.payload.id"
                            v-model="w.payload[f.prop]"
                            :type="f.type"
                            :placeholder="f.placeholder"
                            :options="f.options"
                            @focus="setMainAction(w.ref, 'save')"
                            v-if="f.type !== 'select-multiple'"
                        ></multi-field>
                        <select
                            v-else=""
                            multiple
                            v-model="w.payload[f.prop]"
                            class="form-control form-control-sm taller"
                            @focus="setMainAction(w.ref, 'save')"
                        >
                            <option v-for="o in f.options" :value="o.value">{{ o.title }}</option>
                        </select>
                    </div>
                    <div class="col-1 px-0">
                        <input-actions-widget
                            :widget="w"
                            :main="w.mainAction"
                            @cancel-widget-edit="cancelWidgetEdit(w.ref)"
                            @save-widget-new-entry="saveWidgetNewEntry(w.ref)"
                            @delete-widget-entry="deleteWidgetEntry(w.ref)"
                        ></input-actions-widget>
                    </div>
                </form>
            </template>
        </table-input-widget>

        <!--INSURANCE PROVIDERS-->
        <table-input-widget
            :key="widgetInsuranceProviders.key"
            :no-collapse="true"
            :loading="widgetInsuranceProviders.loading"
            :title="widgetInsuranceProviders.title"
            :fields="widgetInsuranceProviders.fields"
            :data-source="fundingSourcesType('insurance_provider')"
            :summarize="true"
            :no-selected="true"
            @delete="deleteWidgetEntry('widgetInsuranceProviders', $event)"
            @edit="editEntry($event, 'fundingSources')"
        >
            <template slot="top">
                <expected-payments
                    :organization="organization"
                    :source-type="`insurance_provider`"
                    :funding-sources="fundingSourcesType('insurance_provider')"
                    :visible="expectedPaymentsVisible === `insurance_provider`"
                    :title="`Insurance Providers Expected Payments`"
                    :codes="widgetCptCodes.dataSource"
                    ref="ep_insurance"
                    @close="setExpectedPaymentsVisible(null)"
                ></expected-payments>
                <span
                    class="d-flex w-100 justify-content-end align-items-center font-weight-bold cursor-pointer text-primary pb-3"
                    @click="setExpectedPaymentsVisible(`insurance_provider`)"
                >
                    Insurance CPT List Clinic Expected Paid
                    <i class="fa fa-angle-right ml-2"></i>
                </span>
            </template>
            <template slot="bottom">
                <div class="d-flex justify-content-end">
                    <a
                        class="btn btn-sm btn-primary text-white font-weight-bold text-capitalize"
                        @click="openModal('insurance_provider')"
                        >Add new insurance provider</a
                    >
                </div>
            </template>
        </table-input-widget>

        <!--SCHOOLS-->
        <table-input-widget
            :key="widgetSchools.key"
            :no-collapse="true"
            :loading="widgetSchools.loading"
            :title="widgetSchools.title"
            :fields="widgetSchools.fields"
            :data-source="fundingSourcesType('school')"
            :summarize="true"
            :no-selected="true"
            @delete="deleteWidgetEntry('widgetSchools', $event)"
            @edit="editEntry($event, 'fundingSources')"
        >
            <template slot="top">
                <expected-payments
                    :organization="organization"
                    :source-type="`school`"
                    :funding-sources="fundingSourcesType('school')"
                    :visible="expectedPaymentsVisible === `school`"
                    :title="`School Expected Payments`"
                    :codes="widgetServiceCodes.dataSource"
                    ref="ep_school"
                    @close="setExpectedPaymentsVisible(null)"
                ></expected-payments>
                <span
                    class="d-flex w-100 justify-content-end align-items-center font-weight-bold cursor-pointer text-primary"
                    @click="openSchoolBlackouts()"
                >
                    School day off schedule
                    <i class="fa fa-angle-right ml-2"></i>
                </span>
                <span
                    class="d-flex w-100 justify-content-end align-items-center font-weight-bold cursor-pointer text-primary pb-3"
                    @click="setExpectedPaymentsVisible(`school`)"
                >
                    School Service Codes Expected Paid
                    <i class="fa fa-angle-right ml-2"></i>
                </span>
                <modal-component
                    :id="`school-blackout`"
                    :visible="schoolBlackoutsVisible"
                    :size="'lg'"
                    @close="closeSchoolBlackouts()"
                    :title="`School Closed Dates`"
                >
                    <school-blackouts
                        :schools="fundingSourcesType('school')"
                        :organization="organization"
                        @schools-updated="isSchoolsUpdated = true"
                    ></school-blackouts>
                </modal-component>
            </template>
            <template slot="bottom">
                <div class="d-flex justify-content-end">
                    <a
                        class="btn btn-sm btn-primary text-white font-weight-bold text-capitalize"
                        @click="openModal('school')"
                        >Add new school</a
                    >
                </div>
            </template>
        </table-input-widget>

        <!--REGIONAL CENTERS-->
        <table-input-widget
            :key="widgetRegionalCenters.key"
            :no-collapse="true"
            :loading="widgetRegionalCenters.loading"
            :title="widgetRegionalCenters.title"
            :fields="widgetRegionalCenters.fields"
            :data-source="fundingSourcesType('regional_center')"
            :summarize="true"
            :no-selected="true"
            @delete="deleteWidgetEntry('widgetRegionalCenters', $event)"
            @edit="editEntry($event, 'fundingSources')"
        >
            <template slot="top">
                <expected-payments
                    :organization="organization"
                    :source-type="`regional_center`"
                    :funding-sources="fundingSourcesType('regional_center')"
                    :visible="expectedPaymentsVisible === `regional_center`"
                    :title="`Regional Center Expected Payments`"
                    :codes="widgetServiceCodes.dataSource"
                    ref="ep_center"
                    @close="setExpectedPaymentsVisible(null)"
                ></expected-payments>
                <span
                    class="d-flex w-100 justify-content-end align-items-center font-weight-bold cursor-pointer text-primary pb-3"
                    @click="setExpectedPaymentsVisible(`regional_center`)"
                >
                    Regional Center Service Codes Expected Paid
                    <i class="fa fa-angle-right ml-2"></i>
                </span>
            </template>
            <template slot="bottom">
                <div class="d-flex justify-content-end">
                    <a
                        class="btn btn-sm btn-primary text-white font-weight-bold text-capitalize"
                        @click="openModal('regional_center')"
                        >Add new regional center</a
                    >
                </div>
            </template>
        </table-input-widget>

        <!--EASTER SEALS-->
        <table-input-widget
            :key="widgetEasterseals.key"
            :no-collapse="true"
            :loading="widgetEasterseals.loading"
            :title="widgetEasterseals.title"
            :fields="widgetEasterseals.fields"
            :data-source="fundingSourcesType('easter_seal')"
            :summarize="true"
            :no-selected="true"
            :text-small="true"
            @delete="deleteWidgetEntry('widgetEasterseals', $event)"
            @edit="editEntry($event, 'fundingSources')"
        >
            <template slot="top">
                <expected-payments
                    :organization="organization"
                    :source-type="`easter_seal`"
                    :funding-sources="fundingSourcesType('easter_seal')"
                    :visible="expectedPaymentsVisible === `easter_seal`"
                    :title="`Easterseals Expected Payments`"
                    :codes="widgetServiceCodes.dataSource"
                    ref="ep_easterseals"
                    @close="setExpectedPaymentsVisible(null)"
                ></expected-payments>
                <span
                    class="d-flex w-100 justify-content-end align-items-center font-weight-bold cursor-pointer text-primary pb-3"
                    @click="setExpectedPaymentsVisible(`easter_seal`)"
                >
                    Easterseals Service Codes Expected Paid
                    <i class="fa fa-angle-right ml-2"></i>
                </span>
            </template>
            <template slot="bottom">
                <div class="d-flex justify-content-end">
                    <a
                        class="btn btn-sm btn-primary text-white font-weight-bold text-capitalize"
                        @click="openModal('easter_seal')"
                        >Add new easterseals</a
                    >
                </div>
            </template>
        </table-input-widget>

        <!-- Modal -->
        <div
            class="modal fade"
            id="addEntry"
            tabindex="-1"
            role="dialog"
            aria-labelledby="addEntry"
            aria-hidden="true"
        >
            <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
                <new-entry
                    @saved-source="updateSources()"
                    :type="newEntryType || entry.type"
                    :entry="entry"
                    :therapist-types="therapistTypes"
                    :organization-id="organization.id"
                    :available-providers="availableProviders"
                ></new-entry>
            </div>
        </div>
    </div>
</template>

<script>
import { fixDatePickers } from "../../plugins/fixDatePickers";
import ModalComponent from "../ui/ModalComponent";
import SchoolBlackouts from "./CptSchoolDetail";
import NewEntry from "./NewEntry";
import ExpectedPayments from "./ExpectedPayments";
import { helpers } from "../../plugins/helpers";
import TableInputWidget from "../ui/TableInputWidget";
import InputActionsWidget from "../ui/InputActionsWidget";
import MultiField from "../ui/MultiField";
import { errors } from "../../plugins/errors";

export default {
    name: "CptFunding",
    props: ["organization"],
    components: {
        NewEntry,
        ModalComponent,
        SchoolBlackouts,
        ExpectedPayments,
        TableInputWidget,
        InputActionsWidget,
        MultiField,
    },
    mixins: [fixDatePickers, errors],
    data() {
        return {
            helpers,
            vue: this,
            loadingSources: false,
            therapistTypes: [],

            widgetCptCodes: {
                ref: "widgetCptCodes",
                key: "cpt-codes",
                loading: false,
                title: "Our CPT Codes & CPT Nicknames",
                fields: [
                    {
                        title: "CPT Code",
                        prop: "code",
                        widthBase12: 2,
                        type: "text",
                        placeholder: "Code",
                    },
                    {
                        title: "CPT Service Nickname",
                        prop: "service_nickname",
                        widthBase12: 3,
                        type: "text",
                        placeholder: "Service nickname",
                    },
                    {
                        title: "CPT Nickname Code",
                        prop: "service_code",
                        widthBase12: 2,
                        type: "text",
                        placeholder: "Service code",
                    },
                    {
                        title: "Titles",
                        prop: "therapist_types",
                        widthBase12: 3,
                        type: "select-multiple",
                        options: [],
                    },
                ],
                endpoint: "/api/organizations/organizationId/cpt_codes",
                payload: {
                    therapist_types: [],
                },
                prop: "cptCodes",
                mainAction: "add",
                dataSource: [],
                tableClass: "small",
                errors: {},
            },

            widgetDiagnosticCodes: {
                ref: "widgetDiagnosticCodes",
                key: "diagnostic-codes",
                loading: false,
                title: "Our Diagnostic Codes & Nicknames",
                fields: [
                    {
                        title: "Diagnostic Code",
                        prop: "code",
                        widthBase12: 4,
                        type: "text",
                        placeholder: "Code",
                    },
                    {
                        title: "Diagnostic Service Nickname",
                        prop: "service_nickname",
                        widthBase12: 2,
                        type: "text",
                        placeholder: "Service nickname",
                    },
                    {
                        title: "Titles",
                        prop: "therapist_types",
                        widthBase12: 4,
                        type: "select-multiple",
                        options: [],
                    },
                ],
                endpoint: "/api/organizations/organizationId/diagnoses",
                payload: {
                    therapist_types: [],
                },
                prop: "diagnosticCodes",
                mainAction: "add",
                dataSource: [],
                tableClass: "small",
                errors: {},
            },

            widgetServiceCodes: {
                ref: "widgetServiceCodes",
                key: "service-codes",
                loading: false,
                title: "Our Service Codes & Nicknames",
                fields: [
                    {
                        title: "Service Code",
                        prop: "code",
                        widthBase12: 2,
                        type: "text",
                        placeholder: "Code",
                    },
                    {
                        title: "Service Nickname",
                        prop: "service_nickname",
                        widthBase12: 3,
                        type: "text",
                        placeholder: "Service nickname",
                    },
                    {
                        title: "Service Nickname Code",
                        prop: "service_code",
                        widthBase12: 2,
                        type: "text",
                        placeholder: "Service code",
                    },
                    {
                        title: "Titles",
                        prop: "therapist_types",
                        widthBase12: 3,
                        type: "select-multiple",
                        options: [],
                    },
                ],
                endpoint: "/api/organizations/organizationId/service_codes",
                payload: {
                    therapist_types: [],
                },
                prop: "serviceCodes",
                mainAction: "add",
                dataSource: [],
                tableClass: "small",
                errors: {},
            },

            fundingSources: [],

            insurance_provider_list: [],
            widgetInsuranceProviders: {
                ref: "widgetInsuranceProviders",
                key: "insurance-providers",
                loading: false,
                title: "Insurance Providers",
                fields: [
                    { title: "Name", prop: "name", widthBase12: 2, type: "text" },
                    { title: "Address", prop: "address1", widthBase12: 3, type: "text" },
                    { title: "Phone", prop: "primary_phone", widthBase12: 2, type: "text" },
                    {
                        title: "Requires supervision",
                        prop: "requires_supervision_therapist_types",
                        widthBase12: 3,
                        type: "select-multiple",
                        options: [],
                    },
                    { title: "Makeup days", prop: "_makeup_days", widthBase12: 2, type: "text" },
                ],
                mainAction: "add",
                tableClass: "small",
            },

            widgetSchools: {
                ref: "widgetSchools",
                key: "school",
                loading: false,
                title: "Schools",
                fields: [
                    { title: "Name", prop: "name", widthBase12: 2, type: "text" },
                    { title: "Address", prop: "address1", widthBase12: 3, type: "text" },
                    { title: "Phone", prop: "primary_phone", widthBase12: 2, type: "text" },
                    {
                        title: "Requires supervision",
                        prop: "requires_supervision_therapist_types",
                        widthBase12: 3,
                        type: "select-multiple",
                        options: [],
                    },
                ],
                mainAction: "add",
                tableClass: "small",
            },

            widgetRegionalCenters: {
                ref: "widgetRegionalCenters",
                key: "regional-center",
                loading: false,
                title: "Regional Centers",
                fields: [
                    { title: "Name", prop: "name", widthBase12: 2, type: "text" },
                    { title: "Address", prop: "address1", widthBase12: 3, type: "text" },
                    { title: "Phone", prop: "primary_phone", widthBase12: 2, type: "text" },
                    {
                        title: "Requires supervision",
                        prop: "requires_supervision_therapist_types",
                        widthBase12: 3,
                        type: "select-multiple",
                        options: [],
                    },
                ],
                mainAction: "add",
                tableClass: "small",
            },

            widgetEasterseals: {
                ref: "widgetEasterseals",
                key: "easterseals",
                loading: false,
                title: "Easterseals",
                fields: [
                    { title: "Name", prop: "name", widthBase12: 2, type: "text" },
                    { title: "Address", prop: "address1", widthBase12: 3, type: "text" },
                    { title: "Phone", prop: "primary_phone", widthBase12: 2, type: "text" },
                    {
                        title: "Requires supervision",
                        prop: "requires_supervision_therapist_types",
                        widthBase12: 3,
                        type: "select-multiple",
                        options: [],
                    },
                ],
                mainAction: "add",
                tableClass: "small",
            },

            availableProviders: [],
            newEntryType: null,
            newEntry: {
                requires_supervision_therapist_types: [],
            },

            entry: {},

            schoolBlackoutsVisible: false,
            expectedPaymentsVisible: null,
        };
    },

    created() {
        this.initialize();
    },

    computed: {
        unavailableTherapistTypes() {
            let codeModifier = this.widgetCptCodes.payload.code;
            if (this.widgetCptCodes.payload.code_modifier) {
                codeModifier = codeModifier + "-" + this.widgetCptCodes.payload.code_modifier;
            }
            const repeatingCptCodes = this.widgetCptCodes.dataSource.filter(
                cpt => cpt.code === codeModifier
            );
            let unavailableTypes = [];
            if (repeatingCptCodes && repeatingCptCodes.length) {
                unavailableTypes = repeatingCptCodes.reduce(
                    (acc, cur) => [...acc, ...cur.therapist_types],
                    []
                );
            }
            return unavailableTypes;
        },
    },

    watch: {
        schoolBlackoutsVisible() {
            setTimeout(this.removePickerMinWidth, 300);
        },
    },

    methods: {
        initialize() {
            this.getTherapistTypes();
            this.getFundingSources();
            ["widgetCptCodes", "widgetDiagnosticCodes", "widgetServiceCodes"].forEach(w => {
                this.getWidgetDataSource(w);
            });
            this.getProviders();
        },

        fundingSourcesType(type) {
            return this.fundingSources.filter(function(fundingSource) {
                return fundingSource.type === type;
            });
        },

        openSchoolBlackouts() {
            this.schoolBlackoutsVisible = true;
        },

        closeSchoolBlackouts() {
            if (this.isSchoolsUpdated) {
                this.getFundingSources();
                this.isSchoolsUpdated = false;
            }
            this.schoolBlackoutsVisible = false;
        },

        openModal(type) {
            this.entry = null;
            this.newEntryType = type;
            $("#addEntry").modal();
        },

        editEntry(entry, dataSource) {
            switch (
                typeof entry //Only needed before replacing all widgets
            ) {
                case "number":
                    const payload = this[dataSource].find(e => e.id === entry);
                    this.entry = Object.assign({}, payload);
                    break;
                case "object":
                    this.entry = Object.assign({}, entry);
            }
            this.newEntryType = entry.type;
            $("#addEntry").modal();
        },

        updateSources() {
            this.getFundingSources();
            $("#addEntry").modal("hide");
        },

        populateTherapistTypeSelects() {
            let therapistTypesSelect = [];
            this.therapistTypes.forEach(t => {
                const therapistType = {};
                therapistType.value = t.title;
                therapistType.title = t.title;
                therapistTypesSelect.push(therapistType);
            });
            this.widgetCptCodes.fields.find(
                f => f.prop === "therapist_types"
            ).options = therapistTypesSelect;
            this.widgetDiagnosticCodes.fields.find(
                f => f.prop === "therapist_types"
            ).options = therapistTypesSelect;
            this.widgetServiceCodes.fields.find(
                f => f.prop === "therapist_types"
            ).options = therapistTypesSelect;
            this.widgetInsuranceProviders.fields.find(
                f => f.prop === "requires_supervision_therapist_types"
            ).options = therapistTypesSelect;
            this.widgetSchools.fields.find(
                f => f.prop === "requires_supervision_therapist_types"
            ).options = therapistTypesSelect;
            this.widgetRegionalCenters.fields.find(
                f => f.prop === "requires_supervision_therapist_types"
            ).options = therapistTypesSelect;
            this.widgetEasterseals.fields.find(
                f => f.prop === "requires_supervision_therapist_types"
            ).options = therapistTypesSelect;
        },

        getTherapistTypes() {
            axios
                .get(`/api/organizations/${this.organization.id}/therapist_types`)
                .then(r => {
                    this.therapistTypes = r.data.data;
                    this.populateTherapistTypeSelects();
                })
                .catch(e => {
                    console.error(e);
                });
        },

        getFundingSources() {
            this.fundingSources = [];
            this.loadingSources = true;
            this.resetExpectedPayments();
            axios
                .get(`/api/organizations/${this.organization.id}/funding/sources`)
                .then(r => {
                    this.loadingSources = false;
                    this.fundingSources = r.data.data;
                    this.fundingSources.forEach(f => {
                        if (f.hasOwnProperty("requires_supervision_therapist_types")) {
                            f.requires_supervision_therapist_types = f.requires_supervision_therapist_types.map(
                                r => r.title
                            );
                        }
                        f._makeup_days = f.makeup_days !== -1 ? f.makeup_days : "Within the Month";
                    });
                })
                .catch(e => {
                    console.error(e);
                });
        },

        resetExpectedPayments() {
            Object.keys(this.$refs).forEach(r => {
                this.$refs[r].isInitialized = false;
            });
        },

        setExpectedPaymentsVisible(type) {
            this.expectedPaymentsVisible = type;
        },

        getProviders() {
            axios
                .get(`/api/organizations/${this.organization.id}/providers`)
                .then(r => {
                    this.availableProviders = r.data.data;
                })
                .catch(e => {
                    console.error(e);
                });
        },

        setMainAction(widgetName, action = "add") {
            this[widgetName].mainAction = action;
        },

        getWidgetDataSource(widgetName) {
            this[widgetName].loading = true;
            axios({
                method: "GET",
                url: this[widgetName].endpoint.replace("organizationId", this.organization.id),
            })
                .then(r => {
                    this[widgetName].loading = false;
                    this[widgetName].dataSource = this.treatSpecificWidgetsAfterFetch(
                        widgetName,
                        r.data.data
                    );
                })
                .catch(e => {
                    this[widgetName].loading = false;
                    console.error(e);
                });
        },

        treatSpecificWidgetsBeforeSave(widgetName) {
            let payload = this[widgetName].payload;
            let errorMessage = "";
            switch (widgetName) {
                case "widgetCptCodes":
                    if (!payload.code) {
                        errorMessage += "The code field is required. <br>";
                    }
                    if (payload.code_modifier) {
                        payload.code = payload.code + "-" + payload.code_modifier;
                    }
                    break;
                case "widgetDiagnosticCodes":
                case "widgetServiceCodes":
                    if (!payload.code) {
                        errorMessage += "The code field is required. <br>";
                    }
                    break;
            }
            if (errorMessage) {
                this.$swal({
                    title: "Error",
                    html: errorMessage,
                    type: "error",
                    confirmButtonText: "Close",
                    confirmButtonColor: "#d64571",
                });
                payload = undefined;
            }
            return payload;
        },

        treatSpecificWidgetsAfterFetch(widgetName, data) {
            switch (widgetName) {
                case "widgetCptCodes":
                case "widgetDiagnosticCodes":
                case "widgetServiceCodes":
                    this[widgetName].payload = {
                        therapist_types: [],
                    };
                    data.forEach(d => (d.therapist_types = d.therapist_types.map(dt => dt.title))); //Takes title from therapist_types objects
                    break;
            }
            return data;
        },

        treatSpecificWidgetsOnError(widgetName) {
            switch (widgetName) {
                case "widgetCptCodes":
                    if (this[widgetName].payload.code) {
                        this[widgetName].payload.code = this[widgetName].payload.code.split("-")[0];
                    }
            }
        },

        treatSpecificWidgetsAfterSave(widgetName) {
            switch (widgetName) {
                case "widgetCptCodes":
                case "widgetDiagnosticCodes":
                case "widgetServiceCodes":
                    this[widgetName].payload = {
                        therapist_types: [],
                    };
                    break;
                default:
                    this[widgetName].payload = {};
            }
        },

        treatSpecificWidgetsBeforeEdit(widgetName, payload) {
            switch (widgetName) {
                case "widgetCptCodes":
                    payload.code_modifier = payload.code.split("-")[1] || "";
                    payload.code = payload.code.split("-")[0];
                    break;
                default:
                    return payload;
            }
            return payload;
        },

        editWidgetEntry(widgetName, id) {
            this.setMainAction(widgetName, "cancel");
            this[widgetName].errors = {};
            let payload = { ...this[widgetName].dataSource.find(w => w.id === id) };
            payload = this.treatSpecificWidgetsBeforeEdit(widgetName, payload);
            if (payload.cantEdit) return;
            this[widgetName].payload = payload;
        },

        cancelWidgetEdit(widgetName) {
            this.setMainAction(widgetName, "add");
            this.$refs[this[widgetName].ref][0].resetSelected();
            switch (widgetName) {
                case "widgetCptCodes":
                case "widgetDiagnosticCodes":
                case "widgetServiceCodes":
                    this[widgetName].payload = {
                        therapist_types: [],
                    };
                    break;
                default:
                    this[widgetName].payload = {};
            }
        },

        saveWidgetNewEntry(widgetName) {
            this.$set(this[widgetName], "errors", {});
            const payload = this.treatSpecificWidgetsBeforeSave(widgetName);
            if (!payload) return;
            axios({
                method: payload.id ? "PATCH" : "POST",
                url:
                    this[widgetName].endpoint.replace("organizationId", this.organization.id) +
                    (payload.id ? `/${payload.id}` : ""),
                data: payload,
            })
                .then(r => {
                    this.treatSpecificWidgetsAfterSave(widgetName);
                    this.getWidgetDataSource(widgetName);
                    this.$refs[this[widgetName].ref][0].resetSelected();
                    this.setMainAction(widgetName, "add");
                })
                .catch(e => {
                    this.treatSpecificWidgetsOnError(widgetName);

                    const errors = _.get(e, "response.data.errors", {});
                    const message = _.get(e, "response.data.message", {});

                    let errorMessage = "";

                    if (
                        typeof errors === "object" &&
                        Object.keys(errors) &&
                        Object.keys(errors).length
                    ) {
                        Object.keys(errors).forEach(key => {
                            errorMessage +=
                                "<b>" +
                                key.charAt(0).toUpperCase() +
                                key.slice(1) +
                                ":</b> " +
                                errors[key] +
                                "<br>";
                        });
                    } else if (typeof message === "string") {
                        errorMessage = message;
                    } else if (isArray(message)) {
                        errorMessage = message.reduce((acc, cur) => acc + cur + "<br>", "");
                    }

                    this.$swal({
                        title: "Error",
                        html: errorMessage,
                        type: "error",
                        confirmButtonText: "Close",
                        confirmButtonColor: "#d64571",
                    });

                    console.error(e);
                    //this.$set(this[widgetName], "errors", e.response.data.errors);
                });
        },

        deleteWidgetEntry(widgetName) {
            axios({
                method: "DELETE",
                url:
                    this[widgetName].endpoint.replace("organizationId", this.organization.id) +
                    "/" +
                    this[widgetName].payload.id,
            })
                .then(r => {
                    this[widgetName].payload = {};
                    this.getWidgetDataSource(widgetName);
                    this.$refs[this[widgetName].ref][0].resetSelected();
                    this.setMainAction(widgetName, "add");
                })
                .catch(e => {
                    console.error(e);
                    this.$set(this[widgetName], "errors", e.response.data.errors);
                });
        },
    },
};
</script>

<style scoped lang="scss">
.hoverable:hover {
    background: #ebebeb;
}
.taller {
    height: 8rem;
    font-size: 85%;
}
</style>
